import * as React from 'react'
import classNames from 'classnames'
import { Combobox, Option } from 'uiComponents/combobox/combobox'
import { DisabledItemHoverInfotip } from 'uiComponents/infotip'
import { SegmentedControl, SegmentOption } from 'uiComponents/segmentedControl/segmentedControl'
import { SkeletonBar } from 'uiComponents/skeleton/skeleton'
import { PRODUCTS } from 'capacity/copy'
import { ProductTypeKey, SaleFilter, TypeFilter, typeLabel } from './rowModel'

const getSaleOptions = (): SegmentOption<SaleFilter>[] => [
    { value: 'all', label: PRODUCTS.filterAll },
    { value: 'yes', label: PRODUCTS.filterYes },
    { value: 'no', label: PRODUCTS.filterNo },
]

interface ProductsToolbarProps {
    addOptions: Option[]
    onAdd: (productId: string) => void
    types: ProductTypeKey[]
    typeFilter: TypeFilter
    onTypeFilter: (value: TypeFilter) => void
    saleFilter: SaleFilter
    onSaleFilter: (value: SaleFilter) => void
    shown: number
    total: number
    loading: boolean
}

export function ProductsToolbar({
    addOptions,
    onAdd,
    types,
    typeFilter,
    onTypeFilter,
    saleFilter,
    onSaleFilter,
    shown,
    total,
    loading,
}: ProductsToolbarProps) {
    const typeOptions = React.useMemo<SegmentOption<TypeFilter>[]>(() => {
        const present = new Set<ProductTypeKey>(types)
        if (typeFilter !== 'all') {
            present.add(typeFilter)
        }
        const sorted = [...present].sort((a, b) => typeLabel(a, true).localeCompare(typeLabel(b, true)))
        return [
            { value: 'all', label: PRODUCTS.filterAll },
            ...sorted.map((type) => ({ value: type as TypeFilter, label: typeLabel(type, true) })),
        ]
    }, [types, typeFilter])

    const noneLeft = !loading && addOptions.length === 0
    const filtersActive = typeFilter !== 'all' || saleFilter !== 'all'

    return (
        <div className="capacity-toolbar">
            <div className="capacity-toolbar-add">
                <DisabledItemHoverInfotip active={noneLeft} infotipText={PRODUCTS.addNoneLeft} width="18em" bottom>
                    <Combobox
                        options={addOptions}
                        value={null}
                        disabled={noneLeft || undefined}
                        searchPlaceholder={PRODUCTS.addPlaceholder}
                        onChange={(next) => {
                            if (typeof next === 'string') {
                                onAdd(next)
                            }
                        }}
                    />
                </DisabledItemHoverInfotip>
            </div>
            {loading ? (
                <div className="capacity-filters" aria-hidden="true">
                    <SkeletonBar width="18rem" height="2rem" />
                </div>
            ) : (
                total > 0 && (
                    <div className="capacity-filters">
                        <div className="capacity-filter">
                            <span className="capacity-filter-label">{PRODUCTS.filterType}</span>
                            <SegmentedControl
                                size="compact"
                                fit
                                ariaLabel={PRODUCTS.filterType}
                                options={typeOptions}
                                value={typeFilter}
                                onChange={onTypeFilter}
                            />
                        </div>
                        <div className="capacity-filter">
                            <span className="capacity-filter-label">{PRODUCTS.filterOnSale}</span>
                            <SegmentedControl
                                size="compact"
                                fit
                                ariaLabel={PRODUCTS.filterOnSale}
                                options={getSaleOptions()}
                                value={saleFilter}
                                onChange={onSaleFilter}
                            />
                        </div>
                        <span
                            className={classNames('capacity-filter-count', { hidden: !filtersActive })}
                            aria-hidden={!filtersActive}
                        >
                            {PRODUCTS.count(shown, total)}
                        </span>
                    </div>
                )
            )}
        </div>
    )
}

interface FilteredEmptyProps {
    typeFilter: TypeFilter
    saleFilter: SaleFilter
    onShowAll: () => void
}

export function FilteredEmpty({ typeFilter, saleFilter, onShowAll }: FilteredEmptyProps) {
    const last = React.useRef<{ type: TypeFilter; sale: SaleFilter }>({ type: 'all', sale: 'all' })
    if (typeFilter !== 'all' || saleFilter !== 'all') {
        last.current = { type: typeFilter, sale: saleFilter }
    }
    const what =
        last.current.type === 'all' ? PRODUCTS.filteredEmptyProducts : typeLabel(last.current.type, true).toLowerCase()
    return (
        <p className="capacity-table-note">
            {PRODUCTS.filteredEmpty(what, last.current.sale)}{' '}
            <button type="button" className="capacity-text-action" onClick={onShowAll}>
                {PRODUCTS.showAll}
            </button>
        </p>
    )
}
